import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Select, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ClockCounterClockwise, Lock, LockOpen, PencilSimple, Plus } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { IDataNoPagination } from '../../types/shared';
import { IUser } from '../../hooks/useUsers';
import { useAuth } from '../../providers/auth';
import { useDialog } from '../../providers/dialog';
import { AcessoAConta } from './AcessoAConta';
import { CamposExtrasDoUsuario } from './CamposExtras';
import {
  centralApi,
  IEmpresaCarteira,
  IGrupoResumo,
  ITelaCatalogo,
  IUsuarioLista,
} from '../../hooks/useCentralPermissoes';
import { EmpresasAvulsas } from './EmpresasAvulsas';
import { UltimosAcessos } from './LogsDeAcesso';
import { UsuarioMcp } from './mcp/UsuarioMcp';
import { DESCRICAO_GERAL, geralVisivel, moduloDaTela, nomeDaTela, rotuloDaAcao } from './modulos';
import {
  AlternarDois,
  AZUL,
  BarraSalvar,
  Busca,
  Cartao,
  Etiqueta,
  Eyebrow,
  MONO,
  TEXTO,
  TEXTO_SUAVE,
} from './ui';

type Escolha = 'grupo' | 'liberar' | 'bloquear';

const GRUPO_ADMIN = 1;
const GRUPO_CLIENTE = 7;

interface ItemPermissao {
  id: number;
  titulo: string;
  subtitulo: string;
  acao: string;
}

const iniciais = (nome: string) =>
  nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase())
    .join('');

/** "2 anos e 3 meses", a partir da data de admissão (mesma conta da antiga lista). */
function tempoDeCasa(admissao?: string) {
  if (!admissao) return null;
  const dias = Math.floor((Date.now() - new Date(admissao).getTime()) / 864e5);
  if (dias < 0) return null;
  const anos = Math.floor(dias / 365);
  const meses = Math.floor((dias % 365) / 30);
  const partes: string[] = [];
  if (anos > 0) partes.push(`${anos} ${anos === 1 ? 'ano' : 'anos'}`);
  if (meses > 0) partes.push(`${meses} ${meses === 1 ? 'mês' : 'meses'}`);
  if (!partes.length) partes.push(`${dias % 30} ${dias % 30 === 1 ? 'dia' : 'dias'}`);
  return partes.join(' e ');
}

export function UsuarioPainel({
  usuario,
  grupos,
  catalogo,
  onAbrirGrupo,
}: {
  usuario: IUsuarioLista;
  grupos: IGrupoResumo[];
  catalogo: ITelaCatalogo[];
  onAbrirGrupo: (grupoId: number) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const showDialog = useDialog();
  const { isAdmin, user: eu } = useAuth();
  const admin = isAdmin();
  const grupo = grupos.find(g => g.id === usuario.grupo_id);
  const nome = usuario.nome || usuario.email;

  // cadastro completo: o PUT de ativar/desativar manda a linha inteira, como a lista antiga
  const { data: cadastro } = useQuery({
    queryKey: ['central', 'usuario-cadastro', usuario.id],
    queryFn: async () =>
      (await api.get<IDataNoPagination<IUser>>(`/api/v1/usuarios/${usuario.id}/`)).data.data,
  });
  const ativo = cadastro?.ativo ?? usuario.ativo;
  const alternarAtivo = useMutation({
    mutationFn: async () => {
      if (!cadastro) throw new Error('cadastro não carregado');
      await api.put(`/api/v1/usuarios/${usuario.id}/`, { ...cadastro, ativo: !ativo });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(ativo ? `${nome} desativado` : `${nome} ativado`);
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? 'Não foi possível alterar o usuário'),
  });
  const casa = tempoDeCasa(cadastro?.data_admissao);
  const departamento = cadastro?.departamento?.nome as string | undefined;

  const { data: excecoes, isLoading: l1 } = useQuery({
    queryKey: ['central', 'usuario-excecoes', usuario.id],
    queryFn: () => centralApi.excecoesDoUsuario(usuario.id),
  });
  const { data: gruposDele, isLoading: l2 } = useQuery({
    queryKey: ['central', 'usuario-grupos', usuario.id],
    queryFn: () => centralApi.gruposDoUsuario(usuario.id),
  });
  const { data: geraisCatalogo = [] } = useQuery({
    queryKey: ['central', 'permissoes-gerais'],
    queryFn: centralApi.permissoesGerais,
  });
  const { data: carteiraUsuario } = useQuery({
    queryKey: ['central', 'usuario-carteira', usuario.id],
    queryFn: () => centralApi.carteiraDoUsuario(usuario.id),
  });
  const { data: carteirasDisponiveis = [] } = useQuery({
    queryKey: ['central', 'carteiras-disponiveis'],
    queryFn: centralApi.carteirasDisponiveis,
  });

  // permissão -> o que ela libera (ação de tela ou permissão geral)
  const itens = useMemo(() => {
    const m = new Map<number, ItemPermissao>();
    catalogo
      .filter(t => t.ativo)
      .forEach(t =>
        t.acoes.forEach(a => {
          if (a.permissao_id)
            m.set(a.permissao_id, {
              id: a.permissao_id,
              titulo: nomeDaTela(t),
              subtitulo: moduloDaTela(t).nome,
              acao: rotuloDaAcao(a.acao),
            });
        }),
      );
    geraisCatalogo
      .filter(p => geralVisivel(p.codigo))
      .forEach(p =>
        m.set(p.id, {
          id: p.id,
          titulo: p.nome,
          subtitulo: DESCRICAO_GERAL[p.codigo] ?? 'Permissão geral',
          acao: 'Geral',
        }),
      );
    return m;
  }, [catalogo, geraisCatalogo]);

  // o que o grupo principal + os adicionais dão juntos (como estão gravados)
  const doGrupo = useMemo(
    () => new Set<number>(gruposDele?.permissoes_dos_grupos ?? []),
    [gruposDele],
  );
  const carteiraGrupo = gruposDele?.carteira_dos_grupos ?? [];

  const principalBase = gruposDele?.grupo_principal_id ?? usuario.grupo_id;
  const adicionaisBase = useMemo(() => gruposDele?.adicionais ?? [], [gruposDele]);
  const [principal, setPrincipal] = useState<number>(usuario.grupo_id);
  const [adicionais, setAdicionais] = useState<number[]>([]);
  useEffect(() => {
    setPrincipal(principalBase);
    setAdicionais(adicionaisBase);
  }, [principalBase, adicionaisBase]);
  const colaboradores = grupos.filter(g => g.id !== GRUPO_CLIENTE);

  const baseEscolhas = useMemo(() => {
    const m: Record<number, Escolha> = {};
    (excecoes?.especificas ?? []).forEach(p => {
      m[p.id] = 'liberar';
    });
    (excecoes?.desativadas ?? []).forEach(p => {
      m[p.id] = 'bloquear';
    });
    return m;
  }, [excecoes]);
  const [escolhas, setEscolhas] = useState<Record<number, Escolha>>({});
  const [linhas, setLinhas] = useState<number[]>([]);
  useEffect(() => {
    setEscolhas(baseEscolhas);
    setLinhas(Object.keys(baseEscolhas).map(Number));
  }, [baseEscolhas]);

  const [carteira, setCarteira] = useState<string[]>([]);
  useEffect(() => setCarteira(carteiraUsuario ?? []), [carteiraUsuario]);

  // empresas liberadas uma a uma só para ele (somam à carteira)
  const { data: avulsasDele, isError: semRotaDeAvulsas } = useQuery({
    queryKey: ['central', 'usuario-empresas', usuario.id],
    queryFn: () => centralApi.empresasDoUsuario(usuario.id),
    retry: false,
  });
  const [avulsas, setAvulsas] = useState<IEmpresaCarteira[]>([]);
  useEffect(() => setAvulsas(avulsasDele ?? []), [avulsasDele]);
  const mudouAvulsas =
    avulsas
      .map(e => e.id)
      .sort()
      .join('|') !==
    (avulsasDele ?? [])
      .map(e => e.id)
      .sort()
      .join('|');

  const [adicionando, setAdicionando] = useState(false);
  const [buscaNova, setBuscaNova] = useState('');

  const mudouExcecoes =
    JSON.stringify(
      Object.entries(escolhas)
        .filter(([, v]) => v !== 'grupo')
        .sort(),
    ) !== JSON.stringify(Object.entries(baseEscolhas).sort());
  const mudouCarteira =
    [...carteira].sort().join('|') !== [...(carteiraUsuario ?? [])].sort().join('|');
  const mudouGrupos =
    principal !== principalBase ||
    [...adicionais].sort().join('|') !== [...adicionaisBase].sort().join('|');
  const mudou = mudouExcecoes || mudouCarteira || mudouGrupos || mudouAvulsas;

  const liberadas = Object.values(escolhas).filter(v => v === 'liberar').length;
  const bloqueadas = Object.values(escolhas).filter(v => v === 'bloquear').length;
  const telasDoGrupoVer = catalogo.filter(
    t =>
      t.ativo &&
      t.acoes.some(a => a.acao === 'view' && a.permissao_id && doGrupo.has(a.permissao_id)),
  ).length;

  const salvar = useMutation({
    mutationFn: async () => {
      if (mudouGrupos) await centralApi.salvarGruposDoUsuario(usuario.id, principal, adicionais);
      if (mudouExcecoes) {
        const lib = Object.entries(escolhas)
          .filter(([, v]) => v === 'liberar')
          .map(([k]) => Number(k));
        const blo = Object.entries(escolhas)
          .filter(([, v]) => v === 'bloquear')
          .map(([k]) => Number(k));
        await centralApi.salvarExcecoesDoUsuario(usuario.id, lib, blo);
      }
      if (mudouCarteira) await centralApi.salvarCarteiraDoUsuario(usuario.id, carteira);
      if (mudouAvulsas)
        await centralApi.salvarEmpresasDoUsuario(
          usuario.id,
          avulsas.map(e => e.id),
        );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      toast.success(`Permissões de ${nome} salvas`);
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? 'Não foi possível salvar as permissões do usuário'),
  });

  const candidatos = useMemo(() => {
    const q = buscaNova.trim().toLowerCase();
    if (q.length < 2) return [];
    return Array.from(itens.values())
      .filter(i => !linhas.includes(i.id))
      .filter(i => `${i.titulo} ${i.acao} ${i.subtitulo}`.toLowerCase().includes(q))
      .slice(0, 12);
  }, [buscaNova, itens, linhas]);

  if (l1 || l2) {
    return (
      <Flex justify="center" py={16}>
        <Spinner color={AZUL} />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="18px">
        <Flex align="center" gap="14px" wrap="wrap">
          <Flex
            w="46px"
            h="46px"
            borderRadius="50%"
            align="center"
            justify="center"
            fontSize="15px"
            fontWeight={700}
            color="#075985"
            bg="#DFF3FF"
          >
            {iniciais(nome)}
          </Flex>
          <Box flex="1 1 260px" minW={0}>
            <Eyebrow>USUÁRIO</Eyebrow>
            <Text
              fontSize="22px"
              lineHeight="28px"
              fontWeight={700}
              letterSpacing="-.3px"
              color={TEXTO}
            >
              {nome}
            </Text>
            <Text fontSize="13px" color="#475569">
              {usuario.email}
            </Text>
            <Flex gap="6px" wrap="wrap" mt="6px" align="center">
              {!ativo && <Etiqueta tom="perigo">inativo</Etiqueta>}
              {departamento && <Etiqueta tom="info">{departamento}</Etiqueta>}
              {casa && (
                <Text fontSize="12px" color={TEXTO_SUAVE}>
                  na casa há {casa}
                </Text>
              )}
            </Flex>
          </Box>
          <Flex gap="8px" wrap="wrap">
            {admin && (
              <Button
                size="sm"
                variant="ghost"
                borderRadius="999px"
                leftIcon={<ClockCounterClockwise size={14} />}
                onClick={() =>
                  navigate(
                    `/configuracoes/logs-de-acesso?${new URLSearchParams({
                      visao: 'permissoes',
                      alvo_tipo: 'usuario',
                      alvo_id: String(usuario.id),
                      alvo_nome: nome,
                    })}`,
                  )
                }
              >
                Histórico
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              borderRadius="999px"
              bg="rgba(255,255,255,.7)"
              leftIcon={<PencilSimple size={14} />}
              onClick={() => navigate(`/configuracoes/usuarios/${usuario.id}/editar`)}
            >
              Editar cadastro
            </Button>
            {usuario.id !== eu?.id && (
              <Button
                size="sm"
                variant="outline"
                borderRadius="999px"
                bg="rgba(255,255,255,.7)"
                color={ativo ? '#991B1B' : '#166534'}
                leftIcon={ativo ? <Lock size={14} /> : <LockOpen size={14} />}
                isDisabled={!cadastro}
                isLoading={alternarAtivo.isPending}
                onClick={() =>
                  showDialog({
                    status: 'info',
                    title: ativo ? 'Desativar usuário' : 'Ativar usuário',
                    description: ativo
                      ? `${nome} deixa de entrar no sistema. Grupo, exceções e carteira ficam guardados.`
                      : `${nome} volta a entrar no sistema com o mesmo grupo e permissões.`,
                    async onConfirm() {
                      await alternarAtivo.mutateAsync();
                    },
                  })
                }
              >
                {ativo ? 'Desativar' : 'Ativar'}
              </Button>
            )}
          </Flex>
        </Flex>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing="12px">
          <Box p="12px 14px" borderRadius="14px" bg="rgba(248,250,252,.85)">
            <Eyebrow>GRUPO</Eyebrow>
            <Flex justify="space-between" align="center" mt="4px" gap="6px">
              <Text fontWeight={700} fontSize="15px">
                {grupo?.nome ?? usuario.grupo_id}
              </Text>
              <Button
                size="xs"
                variant="link"
                color={AZUL}
                onClick={() => onAbrirGrupo(usuario.grupo_id)}
              >
                Abrir grupo
              </Button>
            </Flex>
          </Box>
          <Box p="12px 14px" borderRadius="14px" bg="rgba(248,250,252,.85)">
            <Eyebrow>TELAS DOS GRUPOS</Eyebrow>
            <Text fontFamily={MONO} fontSize="18px" fontWeight={600} mt="4px">
              {usuario.grupo_id === 1 ? 'todas' : telasDoGrupoVer}
            </Text>
          </Box>
          <Box p="12px 14px" borderRadius="14px" bg="rgba(220,252,231,.75)">
            <Eyebrow color="#166534">LIBERADAS A MAIS</Eyebrow>
            <Text fontFamily={MONO} fontSize="18px" fontWeight={600} mt="4px" color="#166534">
              +{liberadas}
            </Text>
          </Box>
          <Box p="12px 14px" borderRadius="14px" bg="rgba(254,226,226,.75)">
            <Eyebrow color="#991B1B">BLOQUEADAS</Eyebrow>
            <Text fontFamily={MONO} fontSize="18px" fontWeight={600} mt="4px" color="#991B1B">
              −{bloqueadas}
            </Text>
          </Box>
        </SimpleGrid>
        {usuario.grupo_id === 1 && (
          <Text
            fontSize="13px"
            color="#075985"
            bg="rgba(223,243,255,.7)"
            p="10px 14px"
            borderRadius="14px"
          >
            Administrador tem acesso a tudo: exceções e carteira não mudam o que ele vê.
          </Text>
        )}
      </Cartao>

      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="14px">
        <Box>
          <Text fontSize="16px" fontWeight={650}>
            Grupos
          </Text>
          <Text fontSize="13px" color={TEXTO_SUAVE}>
            O grupo principal define quem o usuário é. Os adicionais somam telas, ações, permissões
            gerais e carteira.
          </Text>
        </Box>
        <Flex gap="12px" align="center" wrap="wrap">
          <Box minW="220px">
            <Eyebrow>GRUPO PRINCIPAL</Eyebrow>
            <Select
              mt="4px"
              size="sm"
              borderRadius="10px"
              bg="white"
              value={principal}
              aria-label="Grupo principal"
              onChange={e => {
                const novo = Number(e.target.value);
                setPrincipal(novo);
                setAdicionais(prev => prev.filter(g => g !== novo));
              }}
            >
              {colaboradores.map(g => (
                <option key={g.id} value={g.id}>
                  {g.nome}
                </option>
              ))}
            </Select>
          </Box>
          {principal !== principalBase && (
            <Etiqueta tom="aviso">
              Muda de {grupos.find(g => g.id === principalBase)?.nome ?? principalBase} para{' '}
              {grupos.find(g => g.id === principal)?.nome ?? principal}
            </Etiqueta>
          )}
        </Flex>
        <Box>
          <Eyebrow>GRUPOS ADICIONAIS</Eyebrow>
          <Flex wrap="wrap" gap="8px" mt="6px">
            {colaboradores
              .filter(g => g.id !== GRUPO_ADMIN && g.id !== principal)
              .map(g => {
                const on = adicionais.includes(g.id);
                return (
                  <Box
                    as="button"
                    type="button"
                    key={g.id}
                    aria-pressed={on}
                    onClick={() =>
                      setAdicionais(prev => (on ? prev.filter(x => x !== g.id) : [...prev, g.id]))
                    }
                    display="inline-flex"
                    alignItems="center"
                    gap="6px"
                    h="32px"
                    px="12px"
                    borderRadius="999px"
                    fontSize="13px"
                    fontWeight={600}
                    bg={on ? '#DCFCE7' : 'rgba(255,255,255,.6)'}
                    color={on ? '#166534' : '#475569'}
                    boxShadow={
                      on ? 'inset 0 0 0 1px rgba(22,101,52,.2)' : 'inset 0 0 0 1px #CBD5E1'
                    }
                  >
                    {on ? <Check size={14} /> : <Plus size={14} />}
                    {g.nome}
                  </Box>
                );
              })}
          </Flex>
        </Box>
        {mudouGrupos && (
          <Text fontSize="12.5px" color="#92400E">
            O acesso herdado (coluna “No grupo” e carteira) é recalculado depois de salvar.
          </Text>
        )}
      </Cartao>

      <Cartao p={0} overflow="hidden">
        <Flex
          align="center"
          gap="10px"
          wrap="wrap"
          p="16px 20px"
          borderBottom="1px solid rgba(15,23,42,.07)"
        >
          <Text flex="1 1 200px" fontSize="16px" fontWeight={650}>
            Exceções deste usuário
          </Text>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Plus size={14} />}
            onClick={() => setAdicionando(v => !v)}
          >
            Adicionar exceção
          </Button>
        </Flex>
        {adicionando && (
          <Box p="12px 20px" borderBottom="1px solid rgba(15,23,42,.06)" bg="rgba(248,250,252,.6)">
            <Box maxW="420px">
              <Busca
                valor={buscaNova}
                onChange={setBuscaNova}
                placeholder="Buscar tela, ação ou permissão geral"
              />
            </Box>
            <Flex direction="column" mt="8px" gap="4px">
              {candidatos.map(c => (
                <Flex
                  key={c.id}
                  as="button"
                  type="button"
                  textAlign="left"
                  gap="10px"
                  p="8px 10px"
                  borderRadius="10px"
                  _hover={{ bg: 'white' }}
                  onClick={() => {
                    setLinhas(l => [...l, c.id]);
                    setEscolhas(e => ({
                      ...e,
                      [c.id]: doGrupo.has(c.id) ? 'bloquear' : 'liberar',
                    }));
                    setBuscaNova('');
                    setAdicionando(false);
                  }}
                >
                  <Text fontSize="13px" fontWeight={600}>
                    {c.titulo}
                  </Text>
                  <Text fontSize="13px" color={TEXTO_SUAVE}>
                    {c.acao} · {c.subtitulo}
                  </Text>
                </Flex>
              ))}
              {buscaNova.trim().length >= 2 && candidatos.length === 0 && (
                <Text fontSize="12.5px" color={TEXTO_SUAVE}>
                  Nada encontrado.
                </Text>
              )}
            </Flex>
          </Box>
        )}
        <Box overflowX="auto">
          <Box as="table" w="100%" minW="720px" style={{ borderCollapse: 'collapse' }}>
            <Box as="thead">
              <Box
                as="tr"
                fontFamily={MONO}
                fontSize="10px"
                letterSpacing="1px"
                color={TEXTO_SUAVE}
              >
                {['TELA', 'AÇÃO', 'NO GRUPO', 'PARA ESTE USUÁRIO'].map(h => (
                  <Box as="th" key={h} textAlign="left" p="10px 20px">
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box as="tbody">
              {linhas.map(id => {
                const item = itens.get(id) ?? {
                  id,
                  titulo: `Permissão ${id}`,
                  subtitulo: 'fora da Central',
                  acao: '—',
                };
                const noGrupo = doGrupo.has(id);
                return (
                  <Box as="tr" key={id} borderTop="1px solid rgba(15,23,42,.05)">
                    <Box as="td" p="10px 20px">
                      <Text fontSize="13.5px" fontWeight={700}>
                        {item.titulo}
                      </Text>
                      <Text fontSize="12px" color={TEXTO_SUAVE}>
                        {item.subtitulo}
                      </Text>
                    </Box>
                    <Box as="td" p="10px 20px" fontSize="13px">
                      {item.acao}
                    </Box>
                    <Box as="td" p="10px 20px">
                      <Etiqueta tom={noGrupo ? 'sucesso' : 'neutro'}>
                        {noGrupo ? 'Liberada' : 'Sem acesso'}
                      </Etiqueta>
                    </Box>
                    <Box as="td" p="10px 20px" minW="300px">
                      <AlternarDois
                        valor={escolhas[id] ?? 'grupo'}
                        onChange={v => setEscolhas(e => ({ ...e, [id]: v as Escolha }))}
                        opcoes={[
                          { valor: 'grupo', rotulo: 'Seguir grupo' },
                          { valor: 'liberar', rotulo: 'Liberar' },
                          { valor: 'bloquear', rotulo: 'Bloquear' },
                        ]}
                      />
                    </Box>
                  </Box>
                );
              })}
              {linhas.length === 0 && (
                <Box as="tr">
                  <Box as="td" colSpan={4} p="16px 20px" fontSize="13px" color={TEXTO_SUAVE}>
                    Sem exceções: o usuário tem exatamente o que o grupo tem.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Text
          p="12px 20px"
          fontSize="12.5px"
          color={TEXTO_SUAVE}
          borderTop="1px solid rgba(15,23,42,.05)"
        >
          “Seguir grupo” tira a exceção: quando o grupo mudar, o usuário muda junto.
        </Text>
      </Cartao>

      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="12px">
        <Flex justify="space-between" gap="10px" wrap="wrap">
          <Text fontSize="16px" fontWeight={650}>
            Carteira de empresas
          </Text>
          <Text fontSize="12.5px" color={TEXTO_SUAVE}>
            Herdada dos grupos, mais o que for marcado só para ele (soma)
          </Text>
        </Flex>
        <Flex wrap="wrap" gap="8px">
          {carteirasDisponiveis.map(c => {
            const doG = carteiraGrupo.includes(c);
            const dele = carteira.includes(c);
            let estilo = {
              bg: 'rgba(255,255,255,.6)',
              cor: '#475569',
              sombra: 'inset 0 0 0 1px #CBD5E1',
            };
            if (dele)
              estilo = {
                bg: '#DCFCE7',
                cor: '#166534',
                sombra: 'inset 0 0 0 1px rgba(22,101,52,.2)',
              };
            if (doG) estilo = { bg: '#DFF3FF', cor: '#075985', sombra: 'none' };
            return (
              <Box
                as="button"
                type="button"
                key={c}
                disabled={doG}
                onClick={() =>
                  setCarteira(prev => (dele ? prev.filter(x => x !== c) : [...prev, c]))
                }
                display="inline-flex"
                alignItems="center"
                gap="6px"
                h="32px"
                px="12px"
                borderRadius="999px"
                fontSize="13px"
                fontWeight={600}
                cursor={doG ? 'default' : 'pointer'}
                bg={estilo.bg}
                color={estilo.cor}
                boxShadow={estilo.sombra}
              >
                {doG || dele ? <Check size={14} /> : <Plus size={14} />}
                {c}
                {(doG || dele) && (
                  <Box as="span" fontSize="11px" fontWeight={500} opacity={0.85}>
                    {doG ? 'do grupo' : 'só dele'}
                  </Box>
                )}
              </Box>
            );
          })}
        </Flex>
        <Box borderTop="1px solid rgba(15,23,42,.07)" pt="14px">
          <>
            <EmpresasAvulsas
              selecionadas={avulsas}
              onChange={setAvulsas}
              herdadas={gruposDele?.empresas_dos_grupos ?? []}
            />
            {semRotaDeAvulsas && (
              <Text fontSize="12.5px" color="#92400E">
                Pré-visualização: a busca já funciona, mas salvar empresas avulsas depende do
                backend da carteira por empresa, que ainda não está nesta versão.
              </Text>
            )}
          </>
        </Box>
      </Cartao>

      {admin && (
        <Cartao p="18px 22px">
          <CamposExtrasDoUsuario usuarioId={usuario.id} nome={nome} />
        </Cartao>
      )}

      {admin && (
        <Cartao p="18px 22px">
          <UsuarioMcp usuarioId={usuario.id} nome={nome} />
        </Cartao>
      )}

      {admin && usuario.id !== eu?.id && ativo && (
        <Cartao p="18px 22px">
          <AcessoAConta
            usuario={{
              id: usuario.id,
              nome,
              email: usuario.email,
              imagem_url: cadastro?.imagem_url,
            }}
          />
        </Cartao>
      )}

      <Cartao p="18px 22px">
        <UltimosAcessos usuarioId={usuario.id} nome={nome} />
      </Cartao>

      <BarraSalvar
        texto={
          mudou
            ? `Permissões de ${nome} alteradas. Só são gravadas ao salvar.`
            : 'Nenhuma alteração pendente.'
        }
        semMudanca={!mudou}
        salvando={salvar.isPending}
        onDesfazer={() => {
          setEscolhas(baseEscolhas);
          setLinhas(Object.keys(baseEscolhas).map(Number));
          setCarteira(carteiraUsuario ?? []);
          setAvulsas(avulsasDele ?? []);
        }}
        onSalvar={() => salvar.mutate()}
      />
    </Flex>
  );
}
